import * as Sentry from '@sentry/react-router'
import { useRef } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router'
import { Analytics } from './analytics'
import { ContentHeader } from './layout/content-header'
import { NotFound } from './not-found'
import { SharedLayout } from './shared-layout'
import { SupportedLanguages } from '~/config/i18n'
import { Footer } from '~/components-v2/footer/footer'
import { H1, H2, Stack } from '~/components-v2'

const isNotFound = (error: unknown) =>
  isRouteErrorResponse(error) && error.status === 404

export const handleError = (error: unknown) => {
  if (isNotFound(error))
    return {
      title: 'Whoops! This page wandered off.',
      message:
        "We looked high and low but couldn't find what you were after. The link may be out of date, or the page may have moved on to bigger things.",
    }

  if (error instanceof Error)
    return {
      title: "We're sorry, an error occurred",
      message: error.message,
    }

  return {
    title: "We're sorry, an error occurred",
    message: 'An unknown error occurred.',
  }
}

export const ErrorBoundary = () => {
  const error = useRouteError()
  const mainRef = useRef<HTMLDivElement>(null)
  if (!isRouteErrorResponse(error)) {
    Sentry.captureException(error)
  }
  const { title, message } = handleError(error)

  return (
    <SharedLayout lang={SupportedLanguages.English}>
      <Analytics />
      <ContentHeader mainRef={mainRef} />
      <main ref={mainRef} id="main-content">
        {isNotFound(error) ? (
          <NotFound title={title} message={message} />
        ) : (
          <Stack as="section" gap={6} className="error-boundary">
            <H1 fontWeight={600} fontSize="xxxl">
              {title}
            </H1>
            {message && <H2 fontSize="xxl">{message}</H2>}
          </Stack>
        )}
      </main>
      <Footer />
    </SharedLayout>
  )
}
